import { Sine, TimelineLite, TweenLite } from "gsap";
import * as HLF from "html-living-framework";
import { CaptionToken } from "../Data/CaptionToken";
import * as Constants from "../Data/Constants";
import * as SoundEffect from "../Data/SoundEffect";
import * as Topics from "../Data/Topics";
import * as VoiceOver from "../Data/VoiceOver";
import { Button } from "../GameObjects/Button";

export class HieroglyphButton extends Button {
    private static readonly ICON_COUNT: number[] = [ 5, 5, 4, 4 ];
    private static readonly ICON_SPACING: number = 150;

    private _buttonIndex: number;
    private _iconIndex: number;
    private _iconContainer: PIXI.Container;
    private _icons: PIXI.Sprite[];
    private _iconMask: PIXI.Graphics;
    private _scrollPos: number;
    private _iconVoPrefix: string;

    constructor(pCallback: () => void, pIndex: number) {
        super(pCallback, "button0" + pIndex, Constants.SPRITESHEET_HIERO_PREFIX + "00");
        this._buttonIndex = pIndex;
        this._iconIndex = 0;
        this._scrollPos = 0;

        this._iconMask = new PIXI.Graphics();
        this._iconMask.beginFill(0x000000);
        this._iconMask.drawRect(
            -this._image!.width * 0.5,
            -this._image!.height * 0.5 + 3,
            this._image!.width,
            this._image!.height - 6,
        );
        this._iconMask.endFill();
        this._visuals.addChild(this._iconMask);

        this._iconContainer = new PIXI.Container();
        this._iconContainer.mask = this._iconMask;
        this._visuals.addChild(this._iconContainer);

        this._icons = new Array<PIXI.Sprite>(HieroglyphButton.ICON_COUNT[this._buttonIndex]);
        for (let i = 0; i < this._icons.length; ++i) {
            this._icons[i] = HLF.PixiUtils.makeSprite(
                "button0" + this._buttonIndex + "-icon0" + i,
                Constants.SPRITESHEET_HIERO_PREFIX + "00",
            );
            this._iconContainer.addChild(this._icons[i]);
        }
        this.scrollPos = 0;

        this._clickedSFX = SoundEffect.WIND_UP;
        this._iconVoPrefix = VoiceOver.HIERO_BUTTON_PREFIX + "0" + this._buttonIndex + "-";
    }

    public get iconIndex(): number {
        return this._iconIndex;
    }

    private get scrollPos(): number {
        return this._scrollPos;
    }

    private set scrollPos(pValue: number) {
        this._scrollPos = pValue;
        while (this._scrollPos >= HieroglyphButton.ICON_COUNT[this._buttonIndex]) {
            this._scrollPos -= (HieroglyphButton.ICON_COUNT[this._buttonIndex]);
        }
        for (let i = 0; i < this._icons.length; ++i) {
            this._icons[i].position.y = (i - this._scrollPos) * HieroglyphButton.ICON_SPACING;
            if (this._icons[i].position.y <= -HieroglyphButton.ICON_SPACING) {
                this._icons[i].position.y +=
                    HieroglyphButton.ICON_SPACING * HieroglyphButton.ICON_COUNT[this._buttonIndex];
            }
        }
    }

    public onFocusActivated(): void {
        if (this.interactive) {
            this.incrementIconIndex();
            this.updateIcon();
            this._callback();
            this.playClickedSFX();
        }
    }

    public playScrollAnimation(): TweenLite {
        this.interactive = false;
        return TweenLite.to(this, 2.0, {
            scrollPos: this._scrollPos + 20 - (20 % HieroglyphButton.ICON_COUNT[this._buttonIndex]),
            ease: Sine.easeOut,
            onComplete: () => { this.interactive = true; },
        });
    }

    public setupForOnboarding(pIsOnboarding: boolean): void {
        this._hoverVO = pIsOnboarding ? (VoiceOver.HIERO_BUTTON_PREFIX + "0" + this._buttonIndex) : undefined;
    }

    protected onPointerUp(pEvent: PIXI.interaction.InteractionEvent): void {
        if (this._eventData !== undefined) {
            this.incrementIconIndex();
            this.updateIcon();
            super.onPointerUp(pEvent);
        }
    }

    protected updateIcon(): void {
        this.interactive = false;
        TweenLite.to(this, 0.5, {
            scrollPos: this._scrollPos + 1,
            ease: Sine.easeInOut,
            onComplete: () => {
                this.interactive = true;
                if (this._canPlayVo) {
                    const vo: string = this._iconVoPrefix + HLF.NumberUtils.addZeroPadding(this._iconIndex, 2);
                    PubSub.publishSync(HLF.PLAY_AUDIO, new HLF.AudioToken(
                        vo,
                        1, false, HLF.AudioCategory.VO,
                    ));
                    PubSub.publishSync(Topics.PLAY_CAPTION, new CaptionToken(vo));
                }
            },
        });
    }

    protected playHoverVO(): void {
        super.playHoverVO();
        if (this._hoverVO !== undefined && this._canPlayVo) {
            this._hoverVO = undefined;
        }
    }

    private incrementIconIndex(): void {
        ++this._iconIndex;
        if (this._iconIndex >= HieroglyphButton.ICON_COUNT[this._buttonIndex]) {
            this._iconIndex = 0;
        }
    }
}
